import { useEffect, useRef } from 'react';

// Debounced draft persister shared by useRouteEdits and useDailyChecklistDraft.
//
// Watches `persist` (a useCallback the caller re-creates when its underlying
// draft state changes); after `debounceMs` of quiescence, calls persist().
// Rapid consecutive changes collapse into a single trailing call.
//
// Skip semantics — persist is NOT called when `skipRef.current` is true; the
// flag is consumed (reset to false) on that run. Initialize `skipRef` to true
// so a clean mount never writes, and set it back to true before any imperative
// state change the caller doesn't want echoed back to storage (rehydration,
// mark-complete, cancel — those write/remove directly via localStorageDraft).
export default function useDebouncedDraftWrite(
  persist,
  { debounceMs, skipRef },
) {
  const timer = useRef(null);

  useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      return undefined;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(persist, debounceMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [persist, debounceMs, skipRef]);
}
